import type { Asset, Concept, AssetId, ConceptId, LabelId } from "@storytell/types";
import { batch, createEffect } from "solid-js";
import { type SetStoreFunction, createStore, reconcile } from "solid-js/store";
import type { EntitySyncManager } from "./createEntitySyncManager";

export type ComputedRelationships = {
  assetsForLabel: Record<LabelId, AssetId[]>;
  conceptsForLabel: Record<LabelId, ConceptId[]>;
  labelsForConcept: Record<ConceptId, LabelId[]>;
  assetsForConcept: Record<ConceptId, AssetId[]>;
  conceptsForAsset: Record<AssetId, ConceptId[]>;
};

export const computeSyncRelationships = ({
  assets,
  concepts,
}: {
  assets: EntitySyncManager<Asset & { userId: string }>;
  concepts: EntitySyncManager<Concept & { userId: string }>;
}) => {
  // Computed data that is based on the other entities
  // Useful for caching relatively expensive lookups
  const [computed, setComputed] = createStore<ComputedRelationships>({
    assetsForLabel: {},
    conceptsForLabel: {},
    assetsForConcept: {},
    labelsForConcept: {},
    conceptsForAsset: {},
  });

  // Computing label relationships to assets
  createEffect(() => {
    const assetIds = assets._rawData.ids;
    const newComputed: ComputedRelationships = { ...computed, assetsForLabel: {} };
    for (const assetId of assetIds) {
      const asset = assets.get(assetId);
      if (!asset) continue;
      for (const labelId of asset.labels ?? []) {
        if (!newComputed.assetsForLabel[labelId]) {
          newComputed.assetsForLabel[labelId] = [];
        }
        newComputed.assetsForLabel[labelId].push(assetId);
      }
    }
    setComputed(reconcile(newComputed, { merge: true }));
  });

  // Computing label relationships to concepts and assets <-> concepts
  createEffect(() => {
    const conceptIds = concepts._rawData.ids;

    batch(() => {
      const newComputed: typeof computed = {
        ...computed,
        conceptsForLabel: {},
        conceptsForAsset: {},
        assetsForConcept: {},
        labelsForConcept: {},
      };

      for (const conceptId of conceptIds) {
        const concept = concepts.get(conceptId);
        if (!concept) continue;

        const relatedAssets = new Set<AssetId>();
        for (const seed of concepts.get(conceptId)?.seeds ?? []) {
          for (const chunk of seed.chunks ?? []) {
            relatedAssets.add(chunk.assetId);
            if (!newComputed.conceptsForAsset[chunk.assetId]) {
              newComputed.conceptsForAsset[chunk.assetId] = [];
            }
            if (!newComputed.conceptsForAsset[chunk.assetId].includes(conceptId)) {
              newComputed.conceptsForAsset[chunk.assetId].push(conceptId);
            }
          }
        }

        const relatedLabels = new Set(
          Array.from(relatedAssets).flatMap((assetId) => assets.get(assetId)?.labels ?? []),
        );

        newComputed.assetsForConcept[conceptId] = Array.from(relatedAssets);
        newComputed.labelsForConcept[conceptId] = Array.from(relatedLabels);

        for (const labelId of relatedLabels) {
          if (!newComputed.conceptsForLabel[labelId]) {
            newComputed.conceptsForLabel[labelId] = [];
          }
          newComputed.conceptsForLabel[labelId].push(conceptId);
        }
      }
      setComputed(reconcile(newComputed, { merge: true }));
    });
  });

  const getLabelsForAsset = (assetId: AssetId) => assets.get(assetId)?.labels ?? [];
  const getLabelsForConcept = (conceptId: ConceptId) => computed.labelsForConcept[conceptId] ?? [];
  const getAssetsForLabel = (labelId: LabelId) => computed.assetsForLabel[labelId] ?? [];
  const getConceptsForLabel = (labelId: LabelId) => computed.conceptsForLabel[labelId] ?? [];

  const getAssetsForConcept = (conceptId: ConceptId) => computed.assetsForConcept[conceptId] ?? [];
  const getConceptsForAsset = (assetId: AssetId) => computed.conceptsForAsset[assetId] ?? [];

  const addLabelToAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = assets.get(assetId);
    if (!asset) return;
    assets.set(assetId, {
      ...asset,
      labels: Array.from(new Set([...(asset.labels ?? []), labelId])),
    });
  };
  const removeLabelFromAsset = (assetId: AssetId, labelId: LabelId) => {
    const asset = assets.get(assetId);
    if (!asset) return;
    assets.set(assetId, {
      ...asset,
      labels: (asset.labels ?? []).filter((l) => l !== labelId),
    });
  };

  return {
    getLabelsForAsset,
    getLabelsForConcept,
    getAssetsForLabel,
    getConceptsForLabel,
    getAssetsForConcept,
    getConceptsForAsset,
    addLabelToAsset,
    removeLabelFromAsset,
  };
};
